import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PriceOverride {
  retailPrice: number;
  salePrice: number;
}

type OverrideMap = Map<string, PriceOverride>;

interface Ctx {
  overrides: OverrideMap;
  refetch: () => Promise<void>;
}

const PriceOverridesContext = createContext<Ctx>({
  overrides: new Map(),
  refetch: async () => {},
});

export const PriceOverridesProvider = ({ children }: { children: ReactNode }) => {
  const [overrides, setOverrides] = useState<OverrideMap>(new Map());

  const refetch = useCallback(async () => {
    const { data } = await supabase.from("product_prices").select("product_slug,retail_price,sale_price");
    if (!data) return;
    const map: OverrideMap = new Map();
    for (const row of data as any[]) {
      map.set(row.product_slug, {
        retailPrice: Number(row.retail_price),
        salePrice: Number(row.sale_price),
      });
    }
    setOverrides(map);
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return (
    <PriceOverridesContext.Provider value={{ overrides, refetch }}>
      {children}
    </PriceOverridesContext.Provider>
  );
};

export const usePriceOverrides = () => useContext(PriceOverridesContext);

/** Returns a copy of the product with live price overrides applied (if any). */
export function usePricedProduct<P extends { slug: string; retailPrice: number; salePrice: number }>(
  product: P
): P {
  const { overrides } = usePriceOverrides();
  const o = overrides.get(product.slug);
  if (!o) return product;
  return { ...product, retailPrice: o.retailPrice, salePrice: o.salePrice };
}

/** Apply overrides to a list of products. */
export function usePricedProducts<P extends { slug: string; retailPrice: number; salePrice: number }>(
  products: P[]
): P[] {
  const { overrides } = usePriceOverrides();
  if (overrides.size === 0) return products;
  return products.map((p) => {
    const o = overrides.get(p.slug);
    return o ? { ...p, retailPrice: o.retailPrice, salePrice: o.salePrice } : p;
  });
}
